import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanMatchFn, Router, UrlSegment } from '@angular/router';
import { AuthFacade } from '@core/services/auth-facade';
import { Notifier } from '@core/services/notifier';
import { filter, switchMap, take } from 'rxjs';
import { AuthAccessResult, ensureAuthAccess } from './auth-guard-utils';

const resolveReturnTo = (segments: UrlSegment[], router: Router): string => {
  const navigation = router.currentNavigation();
  const navigationUrl = navigation?.extractedUrl?.toString() ?? navigation?.initialUrl?.toString();
  if (navigationUrl) return navigationUrl;

  const path = segments.map((segment) => segment.path).join('/');
  return path.length > 0 ? `/${path}` : '/';
};

export const authMatchGuard: CanMatchFn = (route, segments) => {
  const authFacade = inject(AuthFacade);
  const router = inject(Router);
  const notifier = inject(Notifier);
  const returnTo = resolveReturnTo(segments, router);

  return toObservable(authFacade.isAuthenticated).pipe(
    filter((value) => value != null),
    take(1),
    switchMap(async (isAuthenticated) => {
      const result = await ensureAuthAccess(authFacade, isAuthenticated, returnTo);
      if (result === AuthAccessResult.TemporaryFailure) {
        notifier.error({
          title: 'Unable to open this page right now.',
          description: 'Authentication validation failed. Please retry.',
        });
      }
      return result === AuthAccessResult.Allowed;
    }),
  );
};
